<template>
  <div class="content-wrapper view-setting p-0">
    <div ref="fileManageContent" class="content"></div>
    <b-card>
      <div class="file-manager-header mb-4">
        <!-- Title and Info Section -->
        <div class="d-flex align-items-center mb-3">
          <h4 class="mb-0 mr-2"><i class="fas fa-file"></i> จัดการไฟล์</h4>
          <b-button
            id="view-file-storage-info-button"
            size="sm"
            variant="outline-info"
            class="ml-2"
          >
            <i class="fas fa-info-circle"></i> ข้อมูลพื้นที่เก็บไฟล์
          </b-button>

          <b-popover
            target="view-file-storage-info-button"
            triggers="hover"
            placement="bottom"
          >
            <template #title
              ><i class="fas fa-database"></i> File Storage Info</template
            >
            <div class="storage-info">
              <div class="storage-row">
                <div class="storage-label">
                  <i class="far fa-calendar-alt"></i>
                  <strong>วันหมดอายุ:</strong>
                </div>
                <div class="storage-value">{{ fileStoreExpiration }}</div>
              </div>
              <div class="storage-row">
                <div class="storage-label">
                  <i class="fas fa-hdd"></i> <strong>ข้อมูลที่เก็บได้:</strong>
                </div>
                <div class="storage-value">{{ fileStoreQuota }}</div>
              </div>
              <div class="storage-row">
                <div class="storage-label">
                  <i class="fas fa-chart-pie"></i>
                  <strong>ข้อมูลที่ใช้:</strong>
                </div>
                <div class="storage-value">{{ fileStoreUsage }}</div>
              </div>
            </div>
          </b-popover>
        </div>

        <!-- Navigation and Filter Bar -->
        <div class="file-control-bar">
          <!-- Tab Navigation and Empty Recycle Bin in same row -->
          <div class="d-flex justify-content-between align-items-center w-100">
            <div class="file-tabs">
              <div class="button-tabs d-flex">
                <b-button
                  :variant="!isDeletedTab ? 'primary' : 'outline-primary'"
                  @click="handleTabChange('active')"
                  class="tab-button mr-2"
                  size="sm"
                >
                  <i class="fas fa-folder-open mr-1"></i> ไฟล์ทั้งหมด
                </b-button>
                <b-button
                  :variant="isDeletedTab ? 'warning' : 'outline-warning'"
                  @click="handleTabChange('deleted')"
                  class="tab-button"
                  size="sm"
                >
                  <i class="fas fa-trash-alt mr-1"></i> ถังขยะ
                </b-button>
              </div>
            </div>

            <!-- Empty Recycle Bin button - pushed all the way right -->
            <div class="file-actions ml-auto">
              <b-button
                v-if="isDeletedTab && getFileList.length > 0"
                variant="danger"
                size="sm"
                class="action-button empty-recycle-bin-btn"
                @click="handleEmptyRecycleBin"
              >
                <i class="fas fa-trash-alt mr-1"></i> ล้างถังขยะ
                <small class="d-block mt-1 font-italic">การดำเนินการนี้ไม่สามารถย้อนกลับได้</small>
              </b-button>
            </div>
          </div>

          <!-- Sort Controls in second row -->
          <div class="sort-controls d-flex align-items-center">
            <b-spinner
              v-if="isSortLoading"
              small
              variant="primary"
              class="mr-2"
            ></b-spinner>
            <b-form-group
              label-for="sort-by"
              label="เรียงโดย:"
              label-size="sm"
              label-cols-sm="auto"
              label-align-sm="right"
              class="mb-0 mr-2"
            >
              <b-form-select
                id="sort-by"
                :options="sortList"
                value-field="value"
                text-field="label"
                v-model="sortBy"
                size="sm"
@change="handleSortChange"
              >
              </b-form-select>
            </b-form-group>

            <b-form-group
              label-for="sort-order"
              label="เรียงจาก:"
              label-size="sm"
              label-cols-sm="auto"
              label-align-sm="right"
              class="mb-0"
            >
              <b-form-select
                id="sort-order"
                :options="orderList"
                value-field="value"
                text-field="label"
                v-model="order"
                size="sm"
                @change="handleOrderChange"
              >
              </b-form-select>
            </b-form-group>
          </div>
        </div>
      </div>
      <!-- File Table -->
      <div class="file-table-wrapper">
        <b-table
          ref="fileManageTable"
          striped
          hover
          responsive
          :fields="fields"
          :items="getFileList"
          class="file-table"
          empty-text="ไม่พบไฟล์"
          show-empty
        >
          <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>

          <template v-slot:cell(src)="row">
            <div
              @click="handleThumbnailClick(row.index)"
              class="thumbnail-container"
            >
              <div
                v-if="row.item.fileType === 'application/pdf'"
                class="text-center file-thumbnail pdf"
              >
                <i class="far fa-file-pdf"></i>
              </div>
              <div
                v-else-if="checkDicom(row.item)"
                class="file-thumbnail dicom"
              >
                <img
                  v-if="dicomThumbnails[row.item.id]"
                  :src="dicomThumbnails[row.item.id]"
                  class="dicom-thumb-img"
                />
                <div v-else class="dicom-placeholder">
                  <div class="text-muted" style="font-size: 14px; font-weight: 600;">DICOM</div>
                </div>
              </div>
              <b-img-lazy
                v-else
                v-bind="imgProps"
                :src="row.item.thumb || row.value"
                class="file-thumbnail"
              ></b-img-lazy>
              <div class="thumbnail-overlay">
                <i class="fas fa-search-plus"></i>
              </div>
            </div>
          </template>

          <template v-slot:cell(fileType)="row">
            <span class="badge badge-light">{{ row.value }}</span>
          </template>

          <!-- Custom cell rendering is not needed since we use the formatter in field definition -->
          <!-- <template v-slot:cell(fileSize)="row">
            <span class="text-muted">{{ row.value ? bytesToSize(row.value) : "0 Bytes" }}</span>
          </template> -->

          <template v-slot:cell(creationDt)="row">
            <span class="text-muted">{{ formatDateTime(row.value) }}</span>
          </template>
          
          <template v-slot:cell(deletedAt)="row">
            <span class="text-muted">{{ formatDateTime(row.value) }}</span>
          </template>

          <template v-slot:cell(button)="row">
            <div
              v-if="row.item.isProcessing"
              class="d-flex justify-content-center"
            >
              <b-spinner
                class="mt-2"
                :variant="isDeletedTab ? 'info' : 'warning'"
                :label="isDeletedTab ? 'Processing...' : 'Moving to trash...'"
              ></b-spinner>
            </div>
            <div v-else class="file-actions-cell">
              <b-button
                v-if="!isDeletedTab"
                class="file-action-btn"
                size="sm"
                variant="outline-warning"
                @click="deleteFile(row.item)"
                title="ย้ายไปถังขยะ"
              >
                <i class="fas fa-trash-alt"></i>
              </b-button>
              <template v-else>
                <b-button
                  class="file-action-btn mr-1"
                  size="sm"
                  variant="outline-info"
                  @click="restoreFile(row.item)"
                  title="กู้คืน"
                >
                  <i class="fas fa-trash-restore"></i> คืนค่า
                </b-button>
                <b-button
                  class="file-action-btn permanent-delete-btn"
                  size="sm"
                  variant="outline-danger"
                  @click="permanentlyDeleteFile(row.item)"
                  title="ลบถาวร"
                >
                  <i class="fas fa-trash-alt"></i> ลบถาวร
                </b-button>
              </template>
            </div>
          </template>

          <template v-slot:custom-foot v-if="isTableLoading">
            <b-tr>
              <b-td colspan="12" class="text-center py-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">กำลังโหลดข้อมูล...</div>
              </b-td>
            </b-tr>
          </template>

          <template v-slot:empty>
            <div class="text-center py-5">
              <i
                class="fas fa-folder-open text-muted"
                style="font-size: 3rem"
              ></i>
              <p class="mt-3 text-muted">
                {{ isDeletedTab ? "ไม่มีไฟล์ในถังขยะ" : "ไม่มีไฟล์" }}
              </p>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>
    <Lightbox :index="lightBoxIndex" :items="lightBoxList" @close="resetList" />
    <Dialog ref="Dialog"></Dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Dialog from "@/components/modal/Dialog";

import VueLightbox from "vue-lightbox-lite";
import "vue-lightbox-lite/dist/index.css";

import bytesToSize from "@/utils/bytesToSize";
import debounce from "@/utils/debounce";
import { isDicomFile, createDicomThumbnail } from "@/utils/daikonUtils";

import moment from "moment";
export default {
  name: "FileMange",
  components: { Lightbox: VueLightbox, Dialog },
  data() {
    return {
      fileList: [],
      baseFields: [
        {
          key: "index",
          label: "",
          class: "text-center",
        },
        {
          key: "creationDt",
          label: "วันที่สร้าง",
        },
        {
          key: "fileSize",
          label: "size",
          formatter: (value) => {
            return value ? bytesToSize(value) : "0 Bytes";
          },
        },
        {
          key: "fileType",
          label: "ประเภท",
          sortable: true,
        },
        {
          key: "src",
          label: "",
        },
        {
          key: "remark",
          label: "รายละเอียด",
        },
        {
          key: "button",
          label: "",
        },
      ],
      offset: 0,
      limit: 50,
      activeTab: 0,
      fileMode: "active", // 'active' or 'deleted'

      // Base sort options that are always available
      baseSortOptions: [
        {
          label: "ขนาดไฟล์",
          value: "fileSize",
        },
        {
          label: "วันที่สร้าง",
          value: "creationDt",
        },
      ],
      orderList: [
        {
          label: "มากไปน้อย",
          value: "desc",
        },
        {
          label: "น้อยไปมาก",
          value: "asc",
        },
      ],
      sortBy: "creationDt",
      order: "desc",
      imgProps: {
        blank: true,

        height: 100,
        class: "m1",
      },
      debouncedHandleScroll: null,
      isTableLoading: false,
      fetchFilesMax: false,

      lightBoxIndex: null,
      lightBoxList: [],

      fileStoreExpiration: "",
      fileStoreQuota: "",
      fileStoreUsage: "",
      
      // Request cancellation
      currentRequest: null,
      scrollRequest: null,
      
      // Loading states
      isSortLoading: false,
      
      // Simple cache for reducing API calls
      cache: new Map(),
      cacheTimeout: 30000, // 30 seconds cache

      // DICOM thumbnails cache
      dicomThumbnails: {},
    };
  },
  watch: {
    getFileList: {
      handler(list) {
        if (list && list.length) {
          this.generateDicomThumbnails(list);
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters({
      getFileList: "moduleMediaFile/getFileList",
      getBranchInfo: "moduleUser/getBranchInfo",
    }),
    isDeletedTab() {
      return this.fileMode === "deleted";
    },
    sortList() {
      // Base sort options
      let options = [...this.baseSortOptions];
      
      // Add deletedAt option only when in recycle bin tab
      if (this.isDeletedTab) {
        options.push({
          label: "วันที่ลบ",
          value: "deletedAt",
        });
      }
      
      return options;
    },
    fields() {
      let fieldList = [...this.baseFields];
      
      // Add deletedAt field only when in recycle bin tab
      if (this.isDeletedTab) {
        // Insert before the "button" field which should be the last column
        fieldList.splice(fieldList.length - 1, 0, {
          key: "deletedAt",
          label: "วันที่ลบ",
        });
      }
      
      return fieldList;
    },
  },
  methods: {
    bytesToSize,
    checkDicom(item) {
      return isDicomFile(item);
    },
    ...mapActions({
      fecthMediaFiles: "moduleMediaFile/fecthMediaFiles",
      deleteMediaFile: "moduleMediaFile/deleteMediaFile",
      restoreMediaFile: "moduleMediaFile/restoreMediaFile",
      permanentlyDeleteMediaFile: "moduleMediaFile/permanentlyDeleteMediaFile",
      emptyRecycleBin: "moduleMediaFile/emptyRecycleBin",
    }),
    handleTabChange(mode) {
      this.fileMode = mode;
      this.offset = 0;
      this.fetchFilesMax = false;
      
      // Set default sorting to deletedAt for deleted files
      if (mode === 'deleted') {
        this.sortBy = 'deletedAt';
      } else {
        // For active files, use creationDt as default
        this.sortBy = 'creationDt';
      }
      
      this.fetchFiles();
    },
    async fetchFiles() {
      // Cancel any existing request
      if (this.currentRequest) {
        this.currentRequest.cancel();
      }
      
      try {
        this.isSortLoading = true;
        
        const params = {
          offset: this.offset,
          limit: this.limit,
          sort: this.sortBy,
          order: this.order,
          softDeleted: this.isDeletedTab ? true : undefined,
        };
        
        // Create cache key
        const cacheKey = `${this.fileMode}-${this.sortBy}-${this.order}-${this.offset}-${this.limit}`;
        
        // Check cache first (only for initial loads, not scroll)
        if (this.offset === 0 && this.cache.has(cacheKey)) {
          const cached = this.cache.get(cacheKey);
          if (Date.now() - cached.timestamp < this.cacheTimeout) {
            this.fileList = cached.data;
            return;
          }
        }
        
        // Create cancellable request
        this.currentRequest = this.fecthMediaFiles({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          params,
        });
        
        const result = await this.currentRequest;
        this.fileList = result;
        
        // Cache the result (only for initial loads)
        if (this.offset === 0) {
          this.cache.set(cacheKey, {
            data: result,
            timestamp: Date.now()
          });
        }
      } catch (error) {
        if (error.name !== 'CancelError') {
          console.error('Fetch files error:', error);
        }
      } finally {
        this.currentRequest = null;
        this.isSortLoading = false;
      }
    },
    
    clearCache() {
      this.cache.clear();
    },
    
    handleSortChange() {
      this.offset = 0;
      this.fetchFilesMax = false;
      this.fetchFiles();
    },
    
    handleOrderChange() {
      this.offset = 0;
      this.fetchFilesMax = false;
      this.fetchFiles();
    },
    async fetchScrollFiles() {
      // Cancel any existing scroll request
      if (this.scrollRequest) {
        this.scrollRequest.cancel();
      }
      
      try {
        this.isTableLoading = true;
        const params = {
          offset: this.offset,
          limit: this.limit,
          sort: this.sortBy,
          order: this.order,
          concat: true,
          softDeleted: this.isDeletedTab ? true : undefined,
        };
        
        // Create cancellable scroll request
        this.scrollRequest = this.fecthMediaFiles({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          params,
        });
        
        const list = await this.scrollRequest;
        if (list.length === 0) this.fetchFilesMax = true;
        this.fileList = [...this.fileList, ...list];
      } catch (err) {
        if (err.name !== 'CancelError') {
          console.error('Fetch scroll files error:', err);
        }
      } finally {
        this.isTableLoading = false;
        this.scrollRequest = null;
      }
    },
    formatDateTime(date) {
      if (!date) return '';
      
      // If it's already a moment object, use it directly
      let momentDate;
      if (moment.isMoment(date)) {
        momentDate = date;
      } else {
        // If it's already a formatted string, return it
        if (typeof date === 'string' && date.includes('/')) {
          return date;
        }
        momentDate = moment(date);
      }
      
      if (!momentDate.isValid()) {
        return 'Invalid Date';
      }
      
      try {
        // Try Thai locale formatting first
        return momentDate.locale("th").format("L LT");
      } catch (error) {
        // Fallback to default formatting if Thai locale fails
        return momentDate.format("DD/MM/YYYY HH:mm");
      }
    },
    formatDate(date) {
      if (!date) return '';
      
      const momentDate = moment(date);
      if (!momentDate.isValid()) {
        return 'Invalid Date';
      }
      
      try {
        // Try Thai locale formatting first
        return momentDate.locale("th").format("LL");
      } catch (error) {
        // Fallback to default formatting if Thai locale fails
        return momentDate.format("DD/MM/YYYY");
      }
    },
    handleScroll(e) {
      if (this.isTableLoading || this.fetchFilesMax) return;
      let docEl = e.target.documentElement;

      if (docEl.scrollTop + docEl.clientHeight >= docEl.scrollHeight - 1) {
        this.offset += this.limit;
        this.fetchScrollFiles();
      }
    },
    handleThumbnailClick(index) {
      const item = this.getFileList[index];
      if (item && isDicomFile(item)) {
        const originalUrl = item.src || item.url;
        const base64Url = btoa(originalUrl);
        const dicomViewerUrl = `/dicom-viewer?data=${base64Url}`;
        window.open(dicomViewerUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      this.lightBoxIndex = index;
      this.lightBoxList = [...this.getFileList];
    },
    resetList() {
      this.lightBoxIndex = null;
      this.lightBoxList = [];
    },
    async generateDicomThumbnails(list) {
      const dicomItems = list.filter(item => isDicomFile(item) && !this.dicomThumbnails[item.id]);
      for (const item of dicomItems) {
        try {
          const url = item.src || item.url;
          const thumbnail = await createDicomThumbnail(url);
          if (thumbnail) {
            this.$set(this.dicomThumbnails, item.id, thumbnail);
          }
        } catch (error) {
          console.warn('Failed to generate DICOM thumbnail for', item.id, error);
        }
      }
    },
    async deleteFile(item) {
      try {
        const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
          "ย้ายไฟล์นี้ไปถังขยะ ?",
          `คุณต้องการย้ายไฟล์นี้ไปถังขยะ`
        );
        if (isConfirmed) {
          this.$set(item, "isProcessing", true);
          const res = await this.deleteMediaFile({
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            id: item.id,
          });
          if (res.status === 200) {
            this.fileList = this.fileList.filter(i => {
              return item.id !== i.id;
            });
            // Clear cache after file operations
            this.clearCache();
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.$set(item, "isProcessing", false);
      }
    },
    async restoreFile(item) {
      try {
        const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
          "กู้คืนไฟล์นี้ ?",
          `คุณต้องการกู้คืนไฟล์นี้`
        );
        if (isConfirmed) {
          this.$set(item, "isProcessing", true);
          const res = await this.restoreMediaFile({
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            id: item.id,
          });
          if (res.status === 200) {
            this.fileList = this.fileList.filter(i => {
              return item.id !== i.id;
            });
            // Clear cache after file operations
            this.clearCache();
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.$set(item, "isProcessing", false);
      }
    },
    async permanentlyDeleteFile(item) {
      try {
        // First confirmation
        const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
          "ลบไฟล์นี้ถาวร ?",
          `คุณต้องการลบไฟล์นี้อย่างถาวร (ไม่สามารถกู้คืนได้อีก)`,
          "warning",
          "ยืนยัน",
          "ยกเลิก"
        );
        
        if (isConfirmed) {
          // Second confirmation
          const { isConfirmed: confirmDelete } = await this.$refs.Dialog.fire({
            title: 'ยืนยันการลบถาวร',
            text: 'การลบถาวรไม่สามารถกู้คืนไฟล์ได้ คุณแน่ใจหรือไม่?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'ลบถาวร',
            cancelButtonText: 'ยกเลิก',
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
          });
          
          if (confirmDelete) {
            this.$set(item, "isProcessing", true);
            const res = await this.permanentlyDeleteMediaFile({
              clinicUrl: this.$route.params.clinicUrl,
              branchUrl: this.$route.params.branchUrl,
              id: item.id,
            });
            if (res.status === 200) {
              this.fileList = this.fileList.filter(i => {
                return item.id !== i.id;
              });
              this.$refs.Dialog.fire({
                icon: 'success',
                title: 'สำเร็จ',
                text: 'ลบไฟล์เรียบร้อยแล้ว',
                showConfirmButton: false,
                timer: 1500
              });
            }
          }
        }
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถลบไฟล์ได้ กรุณาลองใหม่อีกครั้ง',
        });
      } finally {
        this.$set(item, "isProcessing", false);
      }
    },
    async handleEmptyRecycleBin() {
      try {
        // First confirmation with warning
        const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
          "ล้างถังขยะทั้งหมด ?",
          `คุณต้องการลบไฟล์ในถังขยะทั้งหมดอย่างถาวร (ไม่สามารถกู้คืนได้อีก)`,
          "warning",
          "ยืนยัน",
          "ยกเลิก"
        );
        
        if (isConfirmed) {
          // Second confirmation with text input
          const { value: confirmText } = await this.$refs.Dialog.fire({
            title: 'ยืนยันการล้างถังขยะ',
            text: 'พิมพ์คำว่า "ลบทั้งหมด" เพื่อยืนยันการลบไฟล์ทั้งหมดอย่างถาวร',
            input: 'text',
            inputPlaceholder: 'ลบทั้งหมด',
            showCancelButton: true,
            confirmButtonText: 'ยืนยัน',
            cancelButtonText: 'ยกเลิก',
            confirmButtonColor: '#dc3545',
            inputValidator: (value) => {
              if (value !== 'ลบทั้งหมด') {
                return 'กรุณาพิมพ์คำว่า "ลบทั้งหมด" ให้ถูกต้อง';
              }
            }
          });
          
          if (confirmText === 'ลบทั้งหมด') {
            this.isTableLoading = true;
            const res = await this.emptyRecycleBin({
              clinicUrl: this.$route.params.clinicUrl,
              branchUrl: this.$route.params.branchUrl,
            });
            if (res.status === 200) {
              this.fileList = [];
              this.$refs.Dialog.fire({
                icon: 'success',
                title: 'สำเร็จ',
                text: 'ลบไฟล์ในถังขยะทั้งหมดเรียบร้อยแล้ว',
                showConfirmButton: false,
                timer: 1500
              });
            }
          }
        }
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถลบไฟล์ในถังขยะได้ กรุณาลองใหม่อีกครั้ง',
        });
      } finally {
        this.isTableLoading = false;
      }
    },
  },
  async mounted() {
    this.fileStoreExpiration = this.getBranchInfo.fileStoreExpiration
      ? this.formatDate(this.getBranchInfo.fileStoreExpiration)
      : "";
    this.fileStoreQuota =
      this.getBranchInfo && this.getBranchInfo.fileStoreQuota
        ? bytesToSize(this.getBranchInfo.fileStoreQuota)
        : "0 Bytes";
    this.fileStoreUsage =
      this.getBranchInfo && this.getBranchInfo.fileStoreUsage
        ? bytesToSize(this.getBranchInfo.fileStoreUsage)
        : "0 Bytes";

    this.fetchFiles();
    this.debouncedHandleScroll = debounce(this.handleScroll, 1000);
    window.addEventListener("scroll", this.debouncedHandleScroll);
  },
  destroyed() {
    window.removeEventListener("scroll", this.debouncedHandleScroll);
    
    // Cancel any pending requests
    if (this.currentRequest) {
      this.currentRequest.cancel();
    }
    if (this.scrollRequest) {
      this.scrollRequest.cancel();
    }
  },
};
</script>

<style scoped>
.file-manager-header {
  padding-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.file-control-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.file-tabs {
  flex: 0 0 auto;
}

.button-tabs {
  display: flex;
}

.tab-button {
  font-weight: 500;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-actions {
  flex: 0 0 auto;
}

.sort-controls {
  flex: 1 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
}

.action-button {
  font-weight: 500;
}

.empty-recycle-bin-btn {
  padding: 0.5rem 1rem;
  box-shadow: 0 2px 5px rgba(220, 53, 69, 0.25);
  transition: all 0.2s ease;
  min-width: 140px;
  position: relative;
  border: 2px solid #dc3545;
}

.empty-recycle-bin-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.4);
  background-color: #bd2130;
  border-color: #bd2130;
}

.empty-recycle-bin-btn small {
  font-size: 0.7rem;
  opacity: 0.8;
  font-weight: 400;
}

.storage-info {
  min-width: 250px;
}

.storage-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.storage-label {
  margin-right: 1rem;
}

.storage-value {
  font-weight: 500;
}

/* Table and file display styles */
.file-table-wrapper {
  margin-top: 1rem;
}

.file-table th {
  font-weight: 600;
}

.thumbnail-container {
  position: relative;
  cursor: pointer;
  display: inline-block;
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.thumbnail-container:hover {
  transform: scale(1.03);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.file-thumbnail {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.file-thumbnail.pdf {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  width: 100px;
  height: 100px;
}

.file-thumbnail.pdf i {
  font-size: 40px;
  color: #e74c3c;
}

.file-thumbnail.dicom {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #1a1a2e;
  width: 100px;
  height: 100px;
  overflow: hidden;
}

.dicom-thumb-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.dicom-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 4px;
}

.thumbnail-overlay i {
  color: white;
  font-size: 24px;
}

.thumbnail-container:hover .thumbnail-overlay {
  opacity: 1;
}

.file-actions-cell {
  display: flex;
  justify-content: flex-end;
}

.file-action-btn {
  transition: all 0.2s ease;
  opacity: 0.7;
}

.file-action-btn:hover {
  opacity: 1;
  transform: translateY(-2px);
}

.permanent-delete-btn {
  border-width: 2px;
  position: relative;
  font-weight: 500;
}

.permanent-delete-btn:hover {
  background-color: rgba(220, 53, 69, 0.15);
}

/* Badges and misc styles */
.badge {
  padding: 0.5em 0.7em;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 991.98px) {
  .file-control-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .sort-controls {
    width: 100%;
    justify-content: flex-start;
    margin-top: 1rem;
  }

  .file-actions-cell {
    display: flex;
    justify-content: center;
  }
}

@media (max-width: 767.98px) {
  .sort-controls {
    flex-direction: column;
    align-items: flex-start;
  }

  .sort-controls .form-group {
    width: 100%;
    margin-right: 0 !important;
    margin-bottom: 0.5rem !important;
  }

  .file-thumbnail,
  .file-thumbnail.pdf,
  .file-thumbnail.dicom {
    width: 80px;
    height: 80px;
  }

  .file-thumbnail.pdf i {
    font-size: 32px;
  }
}
</style>
